//------------------------------------------------------------------------------
// BFA10104G 共架申込 通信設備
//------------------------------------------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';

import {
  PositiveButton,
  StandardButton,
} from '@/components/atoms/Buttons/PositiveButton';
import DisplayField from '@/components/display-field';
import { requiredValue } from '@/components/validateRule';
import {
  renderTextField,
  renderAutocomplete,
} from '@/components/atoms/CustomPart';
import combo from '@/utils/combo-utils';
import wutil from '@/utils/wijmo-utils';
import PoleItem from '@/utils/pole-item';

// 定数
const FORM_NAME = "BFA10104G-SETUBI";
const LOGH = 'BFA10104G setubi dailog';

/**
 * 設備編集 ダイアログ
 */
function SetubiDialog({
  initialize,
  handleSubmit,
  initValues,
  devCodeMap,
  nearDevMap,
  belowStatMap,
  onDialogClose,
}) {

  // プルダウンリスト
  const devCodeList   = wutil.mapValues(devCodeMap);
  const nearDevList   = wutil.mapValues(nearDevMap);
  const belowStatList = wutil.mapValues(belowStatMap);

  // 初期処理
  React.useEffect(() => {
    initialize({
      ...initValues,

      // コンボボックスの初期値を設定
      setubiCode:  combo.findValue(devCodeList,   initValues?.setubiCode),
      nearSetubi:  combo.findValue(nearDevList,   initValues?.nearSetubi),
      senkaJyokyo: combo.findValue(belowStatList, initValues?.senkaJyokyo),
    });
  }, [initValues]);

  // 確定ボタン処理
  const handleCommit = React.useCallback((values) => {
    const result = {
      recordId:     values?.recordId,
      setubiCode:   values?.setubiCode?.value,          // 架設設備コード
      nearSetubi:   values?.nearSetubi?.value,          // 最接近配電設備
      senkaJyokyo:  values?.senkaJyokyo?.value,         // 線下状況
      tijyoHeight:  wutil.float(values?.tijyoHeight),   // 地上高
      rikakuHaiden: wutil.float(values?.rikakuHaiden),  // 配電設備との離隔
      rikakuUpper:  wutil.float(values?.rikakuUpper),   // 通信線との離隔 上位
      rikakuLower:  wutil.float(values?.rikakuLower),   // 通信線との離隔 下位
    };
    console.info(LOGH, '設備編集確定', result);
    onDialogClose(result);
  }, [onDialogClose]);

  // 取消ボタン処理
  const handleCancel = React.useCallback(() => {
    console.info(LOGH, '設備編集取消');
    onDialogClose(null);
  }, [onDialogClose]);

  //
  // 描画
  //
  return (
    <Dialog
      open={Boolean(initValues)}
      onClose={handleCancel}
      maxWidth="sm" fullWidth
    >
      {/* ヘッダー */}
      <DialogTitle sx={{ py: 1 }}>
        通信設備編集
      </DialogTitle>
      <Divider sx={{ mb: 1 }}/>

      {/* ボディ */}
      <DialogContent sx={{ px: 2, py: 1 }}>
        <form id="setubi-dialog" noValidate autoComplete="off">

          {/* 参照項目 */}
          <Grid container spacing={1}>

            {/* 地区 */}
            <Grid item sx={{ width: '8rem' }}>
              <DisplayField label="地区"
                value={initValues?.areaName}
              />
            </Grid>

            {/* 線路 */}
            <Grid item sx={{ width: '8rem' }}>
              <DisplayField label="冠称"
                value={initValues?.lineName}
              />
            </Grid>

            {/* 電柱番号 */}
            <Grid item sx={{ width: '10rem' }}>
              <DisplayField label="電柱番号"
                value={PoleItem.text({ poleNo: initValues?.poleNo })}
              />
            </Grid>

            {/* 物件番号 */}
            <Grid item sx={{ width: '6rem' }}>
              <DisplayField label="物件番号"
                value={initValues?.bukkenNo?.toString()}
              />
            </Grid>
          </Grid>

          {/* 編集項目 */}
          <Grid container spacing={2} sx={{ my: 2 }}>

            {/* 架設設備コンボボックス */}
            <Grid item sx={{ display: 'flex', gap: 2 }}>
              <Grid item sx={{ width: '20rem' }}>
                <Field name="setubiCode" label="架設設備"
                  component={renderAutocomplete}
                  fullWidth size="small" variant="standard"
                  required validate={[ requiredValue ]}
                  options={devCodeList}
                />
              </Grid>

              {/* 地上高 */}
              <Grid item sx={{ width: '10rem' }}>
                <Field name="tijyoHeight" label="地上高(m)"
                  component={renderTextField} type="number"
                  fullWidth size="small" variant="standard"
                  inputProps={{ step: 0.01, min: 0 }}
                /> 
              </Grid>
            </Grid>

            {/* 最接近配電設備コンボボックス */}
            <Grid item sx={{ display: 'flex', gap: 2 }}>
              <Grid item sx={{ width: '20rem' }}>
                <Field name="nearSetubi" label="最接近配電設備"
                  component={renderAutocomplete}
                  fullWidth size="small" variant="standard"
                  required validate={[ requiredValue ]}
                  options={nearDevList}
                />
              </Grid>

              {/* 配電設備との離隔 */}
              <Grid item sx={{ width: '10rem' }}>
                <Field name="rikakuHaiden" label="配電設備との離隔(m)"
                  component={renderTextField} type="number"
                  fullWidth size="small" variant="standard"
                  inputProps={{ step: 0.1, min: 0 }}
                />
              </Grid>
            </Grid>

            {/* 通信線との離隔 */}
            <Grid item sx={{ display: 'flex', gap: 2 }}>

              {/* 通信線との離隔 上位 */}
              <Box sx={{ width: '13rem' }}>
                <Field name="rikakuUpper" label="通信線との離隔 上位(m)"
                  component={renderTextField} type="number"
                  fullWidth size="small" variant="standard"
                  inputProps={{ step: 0.1, min: 0 }}
                />
              </Box>

              {/* 通信線との離隔 下位 */}
              <Box sx={{ width: '13rem' }}>
                <Field name="rikakuLower" label="通信線との離隔 下位(m)"
                  component={renderTextField} type="number"
                  fullWidth size="small" variant="standard"
                  inputProps={{ step: 0.1, min: 0 }}
                />
              </Box>
            </Grid>

            {/* 線下状況 */}
            <Grid item sx={{ width: '14rem' }}>
              <Field name="senkaJyokyo" label="線下状況"
                component={renderAutocomplete}
                fullWidth size="small" variant="standard"
                options={belowStatList}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      {/* フッター */}
      <DialogActions>

        {/* 取消ボタン */}
        <StandardButton
          size="large" variant="contained"
          onClick={handleCancel}
        >
          キャンセル
        </StandardButton>

        {/* 確定ボタン */}
        <PositiveButton
          type="submit" form="setubi-dialog"
          size="large" variant="contained"
          onClick={handleSubmit(handleCommit)}
        >
          OK
        </PositiveButton>
      </DialogActions>
    </Dialog>
  );
}

// プロパティ
SetubiDialog.propTypes = {
  initValues:   PropTypes.object,   // ダイアログ初期値
  devCodeMap:   PropTypes.object,   // 架設設備マップ
  nearDevMap:   PropTypes.object,   // 接近設備マップ
  belowStatMap: PropTypes.object,   // 線下状況マップ

  // フォーム
  initialize: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,

  // ダイアログクローズ処理
  onDialogClose: PropTypes.func.isRequired,
};

// フォーム接続
SetubiDialog = reduxForm({
  form: FORM_NAME,
  destroyOnUnmount: true,
})(SetubiDialog);

// エクスポート
export default SetubiDialog;
